import { useEffect, useRef, useState } from "react";
import ChevronDownIcon from "../../assets/svg/chevron-down-icon";
import { ROW_OPTIONS, VISIBLE_PAGE_COUNT } from "./utils/constants";
import ChevronLeftIcon from "../../assets/svg/chevron-left-icon";
import ChevronRightIcon from "../../assets/svg/chevron-right-icon";

interface PaginationProps {
  rowsPerPage: number;
  handleRowsPerPageChange: (rowsPerPage: number) => void;
  currentPage: number;
  handlePageChange: (page: number) => void;
  totalPages: number;
  visiblePages?: number[];
  rowsPerPageText?: string;
}
const Pagination = ({
  rowsPerPage,
  handleRowsPerPageChange,
  currentPage,
  handlePageChange,
  totalPages,
  rowsPerPageText = "rows",
}: PaginationProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownButtonRef = useRef<HTMLButtonElement>(null);
  const handlePageChangeRef = useRef(handlePageChange);
  handlePageChangeRef.current = handlePageChange;

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      handlePageChangeRef.current(totalPages);
    }
  }, [currentPage, totalPages]);

  const getVisiblePages = (): (number | "ellipsis")[] => {
    if (totalPages <= VISIBLE_PAGE_COUNT * 2) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const lastStart = totalPages - VISIBLE_PAGE_COUNT + 1;
    const centeredStart = Math.max(1, currentPage - 1);
    const centeredEnd = Math.min(
      totalPages,
      centeredStart + VISIBLE_PAGE_COUNT - 1
    );

    const pages: (number | "ellipsis")[] = [];

    if (currentPage <= totalPages / 2) {
      for (let i = centeredStart; i <= centeredEnd; i++) pages.push(i);
      if (centeredEnd < lastStart - 1) {
        pages.push("ellipsis");
      }
      for (let i = Math.max(centeredEnd + 1, lastStart); i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      for (
        let i = 1;
        i <= Math.min(VISIBLE_PAGE_COUNT, centeredStart - 1);
        i++
      ) {
        pages.push(i);
      }
      if (VISIBLE_PAGE_COUNT < centeredStart - 1) {
        pages.push("ellipsis");
      }
      for (let i = centeredStart; i <= centeredEnd; i++) pages.push(i);
    }

    return pages;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        dropdownButtonRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !dropdownButtonRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const visiblePages = getVisiblePages();

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-[8px]">
        <div className="flex items-center gap-2">
          <span className="text-md font-normal text-dark-gray dark:text-dark-gray-dk">
            Show
          </span>
          <div className="relative">
            <button
              ref={dropdownButtonRef}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center gap-[6px] bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-[6px] px-[8px] py-[3px] shadow-xs-blur"
            >
              <span className="text-md font-semibold text-verba-indigo dark:text-verba-indigo-dk">
                {rowsPerPage}
              </span>
              <ChevronDownIcon
                className={`w-[7px] h-[7px] transition-transform duration-400 text-verba-indigo dark:text-verba-indigo-dk ${
                  isDropdownOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            {isDropdownOpen && (
              <div
                className="bg-popper-bg dark:bg-popper-bg-dk backdrop-blur-[10px] border border-border-gray dark:border-border-gray-dk shadow-popper-blur-effect rounded-[6px] absolute bottom-full mb-1 left-0 z-50 min-w-[60px]"
                ref={dropdownRef}
              >
                <ul className="py-1">
                  {ROW_OPTIONS.map((option) => (
                    <li key={option}>
                      <button
                        onClick={() => {
                          handleRowsPerPageChange(option);
                          setIsDropdownOpen(false);
                        }}
                        className={`w-full text-left px-2 py-1 font-normal text-heading-text dark:text-heading-text-dk text-md hover:bg-hover-effect dark:hover:bg-hover-effect-dk ${
                          rowsPerPage === option
                            ? "font-medium bg-card-highlight dark:bg-card-highlight-dk"
                            : ""
                        }`}
                      >
                        {option}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <span className="text-md font-normal text-dark-gray dark:text-dark-gray-dk">
            {rowsPerPageText}
          </span>
        </div>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center gap-[12px]">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`${
              currentPage === 1
                ? "text-gray-250 dark:text-gray-250-dk cursor-not-allowed"
                : "text-verba-indigo dark:text-verba-indigo-dk cursor-pointer"
            }`}
          >
            <ChevronLeftIcon className="w-[18px] h-[18px]" />
          </button>
          <div className="flex items-center gap-[12px]">
            {visiblePages.map((item, index) =>
              item === "ellipsis" ? (
                <span
                  key={`ellipsis-${index}`}
                  className="text-md font-normal text-data dark:text-data-dk"
                >
                  ...
                </span>
              ) : (
                <button
                  key={item}
                  onClick={() => handlePageChange(item)}
                  className={`${
                    currentPage === item
                      ? "text-md font-semibold text-verba-indigo dark:text-verba-indigo-dk border border-border-gray dark:border-border-gray-dk bg-pure-white dark:bg-pure-white-dk px-[9.5px] py-[3px] rounded-[6px] shadow-xs-blur"
                      : "text-md font-normal text-data dark:text-data-dk"
                  }`}
                >
                  {item}
                </button>
              )
            )}
          </div>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`${
              currentPage === totalPages
                ? "text-gray-250 dark:text-gray-250-dk cursor-not-allowed"
                : "text-verba-indigo dark:text-verba-indigo-dk cursor-pointer"
            }`}
          >
            <ChevronRightIcon className="w-[18px] h-[18px]" />
          </button>
        </div>
      )}
    </div>
  );
};

export default Pagination;
